import { useId, useMemo, useRef, useState } from 'react';
import { useI18n } from '../../lib/i18n';
import { useProducts, type Product } from '../../lib/catalog';
import { useDialogA11y } from '../../lib/useDialogA11y';
import { CloseIcon, SearchIcon } from '../icons';
import { cn } from '../../lib/cn';

interface SendProductModalProps {
  catalogId: string;
  onClose: () => void;
  onSendProduct: (product: Product) => Promise<void>;
  sending?: boolean;
}

export default function SendProductModal({
  catalogId: _catalogId,
  onClose,
  onSendProduct,
  sending = false,
}: SendProductModalProps) {
  const { t } = useI18n();
  const { products, loading } = useProducts();
  const [search, setSearch] = useState('');
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useDialogA11y(true, panelRef, onClose);

  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.sku && p.sku.toLowerCase().includes(q)) ||
        (p.description && p.description.toLowerCase().includes(q)),
    );
  }, [products, search]);

  const handleSend = async (product: Product) => {
    setSelectedProductId(product.id);
    try {
      await onSendProduct(product);
      onClose();
    } finally {
      setSelectedProductId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="flex h-[85vh] max-h-[600px] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-slate-900 border border-slate-200 dark:border-slate-800 animate-in fade-in zoom-in-95"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-slate-800">
          <div className="flex items-center gap-2">
            <span className="text-xl">🛍️</span>
            <h2 id={titleId} className="text-base font-bold text-ink dark:text-white">
              {t('inbox.productModal.title') || 'Send Product'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="grid h-8 w-8 place-items-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
          >
            <CloseIcon size={16} />
          </button>
        </div>

        {/* Search Input */}
        <div className="p-3 border-b border-slate-100 dark:border-slate-800">
          <div className="relative">
            <SearchIcon
              size={15}
              className="absolute top-1/2 -translate-y-1/2 start-3 text-slate-400"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('inbox.productModal.search') || 'Search products by name or SKU...'}
              className="w-full rounded-xl border border-slate-200 bg-surface ps-9 pe-3 py-2 text-xs text-ink outline-none focus:border-primary dark:border-slate-700 dark:bg-slate-800 dark:text-white"
            />
          </div>
        </div>

        {/* Product List */}
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {loading ? (
            <div className="py-12 text-center text-xs text-ink-3 dark:text-slate-400">…</div>
          ) : filteredProducts.length === 0 ? (
            <div className="py-12 text-center text-xs text-ink-3 dark:text-slate-400">
              {t('inbox.productModal.empty') || 'No matching products found.'}
            </div>
          ) : (
            filteredProducts.map((p) => {
              const isSendingThis = sending && selectedProductId === p.id;
              return (
                <div
                  key={p.id}
                  className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-xs hover:border-primary/50 dark:border-slate-800 dark:bg-slate-850 transition"
                >
                  <div className="h-14 w-14 flex-shrink-0 overflow-hidden rounded-lg bg-surface dark:bg-slate-800">
                    {p.imageUrl ? (
                      <img src={p.imageUrl} alt={p.name} className="h-full w-full object-cover" />
                    ) : (
                      <div className="grid h-full place-items-center text-xl text-slate-400">🛍️</div>
                    )}
                  </div>

                  <div className="min-w-0 flex-1 space-y-0.5">
                    <div className="flex items-center gap-2">
                      <b className="truncate text-xs font-bold text-ink dark:text-white">
                        {p.name}
                      </b>
                      <span
                        className={cn(
                          'rounded-full px-1.5 py-0.2 text-[9px] font-bold',
                          p.inStock
                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300'
                            : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
                        )}
                      >
                        {p.inStock ? t('cat.inStock') || 'In stock' : t('cat.outStock') || 'Out of stock'}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-[11px]">
                      <span className="font-extrabold text-primary dark:text-brand">
                        {p.price} {p.currency}
                      </span>
                      {p.sku && (
                        <span className="font-mono text-[10px] text-ink-3 dark:text-slate-400">
                          SKU: {p.sku}
                        </span>
                      )}
                    </div>
                  </div>

                  <button
                    type="button"
                    disabled={sending}
                    onClick={() => void handleSend(p)}
                    className="flex items-center gap-1.5 rounded-lg bg-primary hover:bg-primary-dark px-3 py-1.5 text-xs font-bold text-white transition disabled:opacity-50 shrink-0"
                  >
                    {isSendingThis ? '…' : t('inbox.productModal.send') || 'Send'}
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
